import * as Sentry from '@sentry/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import toaster from 'react-hot-toast';
import { addLaundroworksCustomerSyncJob } from 'components/admin/businesses/LaundroworksIntegration/addLaundroworksCustomerSyncJob';
import {
  ActiveJob,
  getLaundroworksActiveJobs,
} from 'components/admin/businesses/LaundroworksIntegration/getLaundroworksActiveJobs';

export enum JobScope {
  INCREMENTAL = 'incremental',
  FULL = 'full',
}

export interface AddSyncJobPayload {
  businessId: number;
  scope: JobScope;
}

export const useGetLaundroworksActiveJobs = (businessId: number) => {
  const { isLoading, error, data, refetch } = useQuery<
    unknown,
    AxiosError<{ error: string }>,
    ActiveJob[]
  >({
    queryKey: ['laundroworksActiveJobs', businessId],
    queryFn: async () => {
      const response = await getLaundroworksActiveJobs(businessId);
      return response.data?.jobs ?? [];
    },
    retry: 1,
    enabled: !!businessId,
    onError: (error) => {
      Sentry.captureException(error);
      toaster(error?.response?.data?.error ?? 'An error occurred while fetching active jobs', {
        icon: '❌',
      });
    },
  });

  return { isLoading, error, data, refetch };
};

export const useAddLaundroworksCustomerSyncJob = () => {
  return useMutation<unknown, AxiosError<{ error: string }>, AddSyncJobPayload>({
    mutationFn: async ({ businessId, scope }) => {
      return addLaundroworksCustomerSyncJob(businessId, scope);
    },
    onSuccess: async (_, variables) => {
      toaster(
        `${variables.scope === JobScope.FULL ? 'Full' : 'Incremental'} sync job added successfully`,
        {
          icon: '✅',
        },
      );
    },
    onError: (error) => {
      Sentry.captureException(error);
      toaster(error?.response?.data?.error ?? 'An error occurred while adding sync job', {
        icon: '❌',
      });
    },
  });
};
